import React from 'react';
import { useTranslation } from 'react-i18next';

import { EmailLogDetails } from '../../../models/emailLogsDetails';

import { Button, Card, Drawer, Icon, OptionalSection, Status, ToastContext } from '@weezevent/nacre';

import css from './emaillogs.module.css';
import { Products } from '../../../utils/products';
import { LANGUAGES } from '../../../utils/languages';
import { usePermissions } from '../../../models/user/permissions';

export const EmailLogsDetails = ({ organizationId, messageUuid, displayDate, message, onClose, status_config }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();

    const [logDetails, loadingLogDetails] = EmailLogDetails.useApiModel(
        {
            organizationId,
            messageUuid,
            cache: false,
            allow_cache: false,
            strict: true,
            query: {
                ordering: '-action_date'
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId, messageUuid]
    );

    const languageCode = lang => {
        if (!lang.includes('-')) return `${lang}-${lang.toUpperCase()}`;
        return lang;
    };

    return (
        <Drawer
            width={600}
            shouldDisplayCloseButton={true}
            open={messageUuid}
            onClose={onClose}
            header={<Drawer.Header title={t('sauron.emails.email-logs.details.title')} icon="history" iconOffsets={[0, 2]} />}
            footer={
                <Drawer.Footer
                    actions={[
                        <Button key="close" onClick={onClose}>
                            {t('common.cta.close')}
                        </Button>,
                        permissions.hasCapacity('EmailCapacity.LOGS_ADMIN') && (
                            <Button
                                key="display-email"
                                inverted
                                disabled={!message?.preview_url}
                                onClick={() => window.open(message?.preview_url, '_blank', 'noopener, noreferrer')}
                            >
                                {t('sauron.emails.email-logs.details.display-email')} <Icon name="external-link"></Icon>
                            </Button>
                        )
                    ]}
                />
            }
        >
            <Card>
                <Card.Header>
                    <b>{t('sauron.emails.email-logs.details.email-information')}</b>
                </Card.Header>
                <div className={css['content-card-details']}>
                    <Row
                        label={t('sauron.emails.email-logs.details.email-template')}
                        value={
                            <div className={css['product-container']}>
                                {Products[message?.template_application]?.logo}
                                {message?.template_name}
                            </div>
                        }
                    />
                    <Row label={t('sauron.emails.email-logs.details.sender')} value={message?.identity_from_address?.email} />
                    <Row label={t('sauron.emails.email-logs.details.recipient')} value={message?.to[0].email} />
                    <Row label={t('sauron.emails.email-logs.details.language')} value={LANGUAGES.find(lang => lang.locale === languageCode(message.lang))?.text || message.lang} />
                    <Row label={t('sauron.emails.email-logs.details.date-sent')} value={displayDate(message?.send_date)} />
                    <Row label={t('sauron.emails.email-logs.details.last-update')} value={displayDate(message?.last_action?.action_date)} />
                    <Row
                        label={t('sauron.emails.email-logs.details.last-status')}
                        value={
                            <Status
                                color={message.last_action?.event ? status_config.find(status => status.id === message.last_action?.event).color : 'red'}
                                text={t(`sauron.emails.email-logs.status.${message.last_action?.event || 'error'}`)}
                            />
                        }
                    />
                </div>
            </Card>
            <div>
                <div className={css['timeline-header']}>
                    <div className={css['clock-icon']}>
                        <Icon name={'time'} />
                    </div>
                    <b>{t('sauron.emails.email-logs.details.email-history')}</b>
                </div>
                <div className={css['timeline-body']}>
                    {!loadingLogDetails &&
                        logDetails.map((log, index) => (
                            <Card key={index} className={css['detail-card']}>
                                <div style={{ backgroundColor: `var(--button-alpha-${message?.template_application || 'sauron'}-color)`, padding: '12px 15px' }}>
                                    <div className={css['product-container']}>
                                        {Products[message?.template_application]?.logo}
                                        {t('sauron.emails.email-logs.details.email-status')} • {displayDate(log?.action_date)}
                                    </div>
                                </div>
                                <div className={css['detail-card-content']}>
                                    {t('sauron.emails.email-logs.details.new-status')}:
                                    <Status
                                        color={log?.event ? status_config.find(status => status.id === log?.event).color : 'red'}
                                        text={t(`sauron.emails.email-logs.status.${log?.event || 'error'}`)}
                                    />
                                </div>
                                {['bounce', 'hard-bounce'].find(val => val === log?.event) && (
                                    <div className={css['status-description']}>{t(`sauron.emails.email-logs.details.${log.event}-descr`)}</div>
                                )}
                            </Card>
                        ))}
                </div>
                <div className={css['bottom-dot']}></div>

                {permissions.hasCapacity('EmailCapacity.LOGS_ADMIN') && (
                    <div className={css['raw-data']}>
                        <OptionalSection
                            label={
                                <div>
                                    {t('sauron.emails.email-logs.details.display-raw-data')}
                                    <span className={css['admin-label']}>
                                        <Icon name={'user'} />
                                        Admin
                                    </span>
                                </div>
                            }
                        >
                            <div className={css['action-body']}>{JSON.stringify(message?.last_action?.body, null, 2)}</div>
                        </OptionalSection>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

function Row({ label, value }) {
    return (
        <div className={css['row']}>
            <span className={css['label']}>{label}</span>
            <span className={css['value']}>{value}</span>
        </div>
    );
}
